import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaFacebook,
  FaGoogle, 
  FaLinkedin, 
  FaTiktok, 
  FaTwitter, 
  FaWhatsapp, 
  FaPlug,
  FaLink,
  FaRocket,
  FaBolt
} from 'react-icons/fa';

const Integrations = () => {
  const [hoveredLogo, setHoveredLogo] = useState(null);

  // Memoize static data
  const integrations = useMemo(() => [
    { name: 'Meta', icon: FaFacebook, color: '#1877F2', category: 'social' },
    { name: 'Google', icon: FaGoogle, color: '#4285F4', category: 'ads' },
    { name: 'LinkedIn', icon: FaLinkedin, color: '#0077B5', category: 'social' },
    { name: 'TikTok', icon: FaTiktok, color: '#000000', category: 'social' },
    { name: 'Twitter', icon: FaTwitter, color: '#1DA1F2', category: 'social' },
    { name: 'HubSpot', icon: FaPlug, color: '#FF7A59', category: 'crm' },
    { name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366', category: 'messaging' },
    { name: 'Zapier', icon: FaPlug, color: '#FF4A00', category: 'automation' },
    { name: 'Mailchimp', icon: FaPlug, color: '#FFE01B', category: 'email' },
    { name: 'Klaviyo', icon: FaPlug, color: '#6C5CE7', category: 'email' },
  ], []);

  // Respect reduced motion
  const prefersReducedMotion = useMemo(() => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, []
  );

  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section className="relative overflow-hidden bg-white py-32">
      {/* Animated network background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Static grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div 
            style={{
              backgroundImage: 'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
              transform: 'translateZ(0)',
            }}
            className="absolute inset-0"
          />
        </div>

        {/* Floating gradient orbs - CSS anims */}
        <div 
          className="absolute top-20 left-1/4 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl [animation:css-orb-float 8s ease-in-out infinite]"
          style={{ transform: 'translateZ(0)' }}
        />
        <div 
          className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl [animation:css-orb-float 10s ease-in-out infinite-reverse]"
          style={{ transform: 'translateZ(0)' }}
        />

        {/* Simplified connection lines - CSS */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none">
          {integrations.map((_, i) => (
            <line
              key={i}
              x1={`${20 + (i * 8)}%`}
              y1="20%"
              x2={`${30 + (i * 7)}%`}
              y2="80%"
              stroke="url(#lineGradient)"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="[animation:css-line-dash 3s ease-in-out infinite]"
              style={{ 
                animationDelay: `${i * 0.3}s`,
                transform: 'translateZ(0)',
              }}
            />
          ))}
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="50%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 mb-6"
          >
            <motion.div
              className="will-change-transform"
              style={{ transform: 'translateZ(0)' }}
              animate={prefersReducedMotion ? false : { rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <FaLink className="text-purple-600" />
            </motion.div>
            <span className="text-sm font-bold text-purple-900">Seamless Connections</span>
          </motion.div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
            <span className="block text-slate-900">Ecosystem</span>
            <span className="block bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent animate-gradient">
              Symphony
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Weave your tools into one harmonious flow—sync leads, automate whispers, trigger workflows that hum with intelligence.
          </p>
        </motion.div>

        {/* Central Hub Visualization */}
        <div className="relative max-w-5xl mx-auto mb-24">
          {/* Central hub - CSS rotations */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div 
              className="relative [animation:css-hub-rotate 30s linear infinite]"
              style={{ transform: 'translateZ(0)' }}
            >
              {/* Outer ring */}
              <div className="w-40 h-40 rounded-full border-4 border-dashed border-purple-300/50" />
              
              {/* Middle ring */}
              <div 
                className="absolute inset-4 rounded-full border-4 border-dashed border-pink-300/50 [animation:css-hub-rotate-reverse 20s linear infinite]"
                style={{ transform: 'translateZ(0)' }}
              />
              
              {/* Inner core */}
              <div 
                className="absolute inset-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 shadow-2xl flex items-center justify-center [animation:css-hub-rotate-reverse 15s linear infinite]"
                style={{ transform: 'translateZ(0)' }}
              >
                <div 
                  className="[animation:css-hub-rotate 15s linear infinite]"
                  style={{ transform: 'translateZ(0)' }}
                >
                  <FaRocket className="text-white text-4xl" />
                </div>
              </div>

              {/* Pulsing effect */}
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30 will-change-transform"
                style={{ transform: 'translateZ(0)' }}
                animate={prefersReducedMotion ? false : {
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 0, 0.5]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </div>

          {/* Orbiting integration logos */}
          <div className="relative h-[600px]">
            {integrations.map((integration, idx) => {
              const angle = (idx * 360) / integrations.length;
              const radius = 250;
              const x = Math.cos((angle * Math.PI) / 180) * radius;
              const y = Math.sin((angle * Math.PI) / 180) * radius;

              return (
                <React.Fragment key={idx}>
                  {/* Connection line */}
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none will-change-transform"
                    style={{ transform: 'translateZ(0)' }}
                    initial={prefersReducedMotion ? false : { opacity: 0 }}
                    animate={prefersReducedMotion ? false : { 
                      opacity: hoveredLogo === integration.name ? 1 : 0.2 
                    }}
                  >
                    <line
                      x1="50%"
                      y1="50%"
                      x2={`calc(50% + ${x}px)`}
                      y2={`calc(50% + ${y}px)`}
                      stroke={integration.color}
                      strokeWidth="3"
                      strokeDasharray="5,5"
                      initial={prefersReducedMotion ? false : { pathLength: 0 }}
                      animate={prefersReducedMotion ? false : { 
                        pathLength: hoveredLogo === integration.name ? 1 : 0.5,
                        strokeDashoffset: [0, -10]
                      }}
                      transition={{
                        pathLength: { duration: 0.5 },
                        strokeDashoffset: { duration: 2, repeat: Infinity, ease: "linear" }
                      }}
                    />
                  </svg>

                  {/* Integration logo */}
                  <motion.div
                    className="absolute top-1/2 left-1/2 cursor-pointer will-change-transform"
                    style={{ 
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: 'translateZ(0)',
                    }}
                    initial={prefersReducedMotion ? false : { opacity: 0, scale: 0 }}
                    whileInView={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1, type: "spring", stiffness: 200 }}
                    onMouseEnter={() => setHoveredLogo(integration.name)}
                    onMouseLeave={() => setHoveredLogo(null)}
                  >
                    <motion.div
                      className="relative"
                      whileHover={prefersReducedMotion ? false : { scale: 1.3, zIndex: 30 }}
                      animate={prefersReducedMotion ? false : {
                        y: hoveredLogo === integration.name ? -10 : 0,
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {/* Glow effect */}
                      <AnimatePresence>
                        {hoveredLogo === integration.name && (
                          <motion.div
                            className="absolute inset-0 rounded-2xl blur-xl will-change-transform"
                            style={{ 
                              backgroundColor: integration.color,
                              transform: 'translateZ(0)',
                            }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 0.6, scale: 1.5 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                          />
                        )}
                      </AnimatePresence>

                      {/* Logo card */}
                      <motion.div
                        className="relative w-20 h-20 rounded-2xl shadow-xl border-4 border-white flex items-center justify-center will-change-transform"
                        style={{ 
                          backgroundColor: hoveredLogo === integration.name ? integration.color : 'white',
                          color: hoveredLogo === integration.name ? 'white' : integration.color,
                          transform: 'translateZ(0)',
                        }}
                        animate={prefersReducedMotion ? false : {
                          rotate: hoveredLogo === integration.name ? 360 : 0
                        }}
                        transition={{ duration: 0.6 }}
                      >
                        <integration.icon size={32} />

                        {/* Active indicator */}
                        <AnimatePresence>
                          {hoveredLogo === integration.name && (
                            <motion.div
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 border-2 border-white flex items-center justify-center will-change-transform"
                              style={{ transform: 'translateZ(0)' }}
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 500 }}
                            >
                              <FaBolt className="text-white text-xs" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      {/* Tooltip */}
                      <motion.div
                        className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap"
                        initial={prefersReducedMotion ? false : { opacity: 0, y: -5 }}
                        animate={prefersReducedMotion ? false : { 
                          opacity: hoveredLogo === integration.name ? 1 : 0,
                          y: hoveredLogo === integration.name ? 0 : -5
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <div 
                          className="px-4 py-2 rounded-lg text-white text-sm font-bold shadow-lg"
                          style={{ backgroundColor: integration.color }}
                        >
                          {integration.name}
                          <div 
                            className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                            style={{ backgroundColor: integration.color }}
                          />
                        </div>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Feature cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {[
            {
              icon: <FaLink />,
              title: 'One-Click Setup',
              desc: 'Connect your entire stack in seconds with pre-built integrations',
              color: 'from-purple-500 to-pink-500'
            },
            {
              icon: <FaBolt />,
              title: 'Real-Time Sync',
              desc: 'Leads flow instantly across platforms—no delays, no manual work',
              color: 'from-pink-500 to-orange-500'
            },
            {
              icon: <FaRocket />,
              title: 'Smart Automation',
              desc: 'Trigger workflows automatically based on lead behavior and scores',
              color: 'from-blue-500 to-cyan-500'
            }
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={prefersReducedMotion ? false : { y: -10, scale: 1.03 }}
              className="relative bg-white rounded-2xl p-8 shadow-xl border-2 border-slate-100 overflow-hidden group cursor-pointer will-change-transform"
              style={{ transform: 'translateZ(0)' }}
            >
              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
              
              {/* Icon */}
              <motion.div
                className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 text-white shadow-lg will-change-transform`}
                style={{ transform: 'translateZ(0)' }}
                whileHover={prefersReducedMotion ? false : { rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                {feature.icon}
              </motion.div>

              {/* Content */}
              <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
              <p className="text-slate-600 leading-relaxed">{feature.desc}</p>

              {/* Decorative element */}
              <div className={`absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br ${feature.color} opacity-5 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
            </motion.div>
          ))}
        </motion.div>

        {/* Infinite scroll logos - Bottom - CSS anim */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={prefersReducedMotion ? false : { opacity: 1 }}
          className="relative overflow-hidden py-12 bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50 rounded-3xl"
        >
          <div className="relative">
            <div 
              className="flex [animation:css-infinite-scroll 30s linear infinite]"
              style={{ transform: 'translateZ(0)' }}
            >
              {[...integrations, ...integrations].map((integration, idx) => (
                <motion.div
                  key={idx}
                  className="flex-shrink-0 mx-6 will-change-transform"
                  style={{ transform: 'translateZ(0)' }}
                  whileHover={prefersReducedMotion ? false : { scale: 1.2, y: -5 }}
                >
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg border-2 border-white"
                    style={{ backgroundColor: `${integration.color}15` }}
                  >
                    <integration.icon 
                      size={24} 
                      style={{ color: integration.color }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Fade edges */}
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-purple-50 to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-blue-50 to-transparent pointer-events-none" />
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-slate-600 text-lg mb-6 max-w-2xl mx-auto">
            Effortlessly bridge your stack—leads sync in seconds, messages automate like clockwork, workflows ignite on contact.
          </p>
        
        </motion.div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes css-orb-float {
          0%, 100% { 
            transform: scale(1) translate(0, 0); 
            opacity: 0.3; 
          }
          50% { 
            transform: scale(1.2) translate(50px, 30px); 
            opacity: 0.6; 
          }
        }
        @keyframes css-orb-float-reverse {
          0%, 100% { 
            transform: scale(1.2) translate(0, 0); 
            opacity: 0.3; 
          }
          50% { 
            transform: scale(1) translate(-50px, -30px); 
            opacity: 0.6; 
          }
        }
        @keyframes css-line-dash {
          0%, 100% { 
            stroke-dashoffset: 0; 
            pathLength: 0.5; 
          }
          50% { 
            stroke-dashoffset: -10; 
            pathLength: 1; 
          }
        }
        @keyframes css-hub-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes css-hub-rotate-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes css-infinite-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default Integrations;